import * as React from "react";

import {
  AccessReportsWizardAccessCriteria,
  AccessReportsWizardTemplate,
  buildStandard,
} from "@app/access-reports/shared";
import {
  AccessReportsWizardEditNetworks,
  AccessReportParametersModel,
} from "@app/access-reports/wizard";
import { isEqual } from "lodash";
import { FlyoutLink } from "@app/shared/Flyouts";
import { ColumnConfig, Waffles, wafflesKeyColumnName } from "@app/shared/waffles";
import { QESRadioButton } from "@app/shared/qes-standards";
import { JSX } from "react";

interface AccessReportsWizardStepAccessAnalysisProps {
  accessAnalysisWizardForm: React.ReactNode;
  accessReportParameters: AccessReportParametersModel;
  accessReportsWizardTemplate: AccessReportsWizardTemplate;
  onAccessCriteriaCheckboxToggle: (selectedKeys: string[], selectedGroupKey: string) => void;
  onGroupedAccessCriterionClick: (groupKey: string, selectedKeys: string[]) => void;
  onProductChange: (accessCriteria: AccessReportsWizardAccessCriteria) => void;
}

interface AccessReportsWizardStepAccessAnalysisState {
  groupedAccessCriterion: Map<string, AccessReportsWizardAccessCriteria[]>;
  selectedAccessCriterion: AccessReportsWizardAccessCriteria[];
  selectedGroupKey: string;
  sidePanelVisible: boolean;
}

interface AccessAnalysisTableData {
  [wafflesKeyColumnName]: string;
  edit: JSX.Element;
  standards: string;
  type: string;
}

class AccessReportsWizardStepAccessAnalysis extends React.Component<AccessReportsWizardStepAccessAnalysisProps, AccessReportsWizardStepAccessAnalysisState> {
  private ungroupedKey = "Ungrouped";
  private tablesColumnConfig = {};

  constructor(props: AccessReportsWizardStepAccessAnalysisProps) {
    super(props);

    this.tablesColumnConfig = this.getColumnConfig();

    this.state = {
      groupedAccessCriterion: new Map<string, AccessReportsWizardAccessCriteria[]>(),
      selectedAccessCriterion: [],
      selectedGroupKey: null,
      sidePanelVisible: false,
    };
  }

  public componentDidMount() {
    if (!!this.props.accessReportParameters.product?.accessCriterion) {
      this.buildGroupedAccessCriterion();
    }
  }

  public componentDidUpdate(prevProps: AccessReportsWizardStepAccessAnalysisProps) {
    if (!isEqual(prevProps.accessReportParameters.product.accessCriterion, this.props.accessReportParameters.product.accessCriterion)) {
      this.buildGroupedAccessCriterion();
    }
  }

  public render() {
    return (
      <React.Fragment>
        <div className="row">
          <div className="col-12">
            <h4 className="header-s mb-3">
              Product: {(this.props.accessReportParameters && this.props.accessReportParameters.product) && this.props.accessReportParameters.product.name}
            </h4>
          </div>
        </div>

        {this.props.accessAnalysisWizardForm}

        <div className="row">
          <div className="col-12">
            <hr className="mt0" />
          </div>
        </div>

        {(!!this.props.accessReportParameters.standard && !!this.props.accessReportParameters.calculationMethod) ?
          this.buildAccessCriterionTables() :
          <div className="row">
            <div className="col-12">
              <p className="text-center copy-text-l">
                Please select an access standard and a calculation method.
              </p>
            </div>
          </div>
        }
      </React.Fragment>
    );
  }

  private buildAccessCriterionTables = (): React.ReactNode => {
    return (
      <React.Fragment>
        {(this.state.groupedAccessCriterion.keys().next().value !== this.ungroupedKey && this.state.groupedAccessCriterion.size !== 1) &&
          <div className="row">
            <div className="col-12">
              <label className="label-m mb-3">
                Please select one of the following groups:
              </label>
            </div>
          </div>
        }

        {Array.from(this.state.groupedAccessCriterion.keys()).sort((a, b) => (this.getSortValue(a, b))).map((groupKey, index) => (
          <React.Fragment key={index}>
            {groupKey === this.ungroupedKey ?
              <div className="row">
                <div className="col-12">
                  <label className="label-s mb-3">
                    Select ungrouped access criterion:
                  </label>
                </div>
              </div> :
              <div className="row">
                <div className="col-12">
                  <QESRadioButton
                    label={groupKey}
                    labelClass="label-s"
                    radioContainerClassName="ml-n3"
                    checked={this.state.selectedGroupKey === groupKey}
                    className="qes-radio"
                    testId={`access-reports-wizard__access-analysis--grouped-access-criteria-${index}`}
                    onChange={() => this.onGroupedAccessCriterionClick(groupKey)}
                    name="access-analysis-groups"
                  />
                </div>
              </div>
            }

            <div className="row">
              <div className="col-12">
                <div className="ml-5">
                  {this.buildAccessCriterionTable(groupKey, index)}
                </div>
              </div>
            </div>
          </React.Fragment>
        ))}
      </React.Fragment>
    );
  }

  private buildAccessCriterionTable = (groupKey: string, groupIndex: number): React.ReactNode => {
    const accessCriterion: AccessReportsWizardAccessCriteria[] = this.state.groupedAccessCriterion.get(groupKey);
    const filteredAccessCriterion: AccessReportsWizardAccessCriteria[] = accessCriterion && accessCriterion.filter((x) => !x.hidden);

    if (!!filteredAccessCriterion && filteredAccessCriterion.length) {
      const data: AccessAnalysisTableData[] = filteredAccessCriterion.map((accessCriteria, index) => ({
        /* eslint-disable sort-keys */
        [wafflesKeyColumnName]: accessCriteria.id.toString(),
        type: accessCriteria.name,
        standards: buildStandard(accessCriteria, this.props.accessReportParameters),
        edit: (
          <FlyoutLink
            classNames="qes-button-secondary"
            content={{
              body: this.renderEditNetworkSidePanel(accessCriteria) as JSX.Element,
              title: "Edit Network",
            }}
            panelId={`access-reports-wizard__access-analysis--access-criteria-group-${groupIndex}-edit-${index}`}
            isVisible={this.state.sidePanelVisible}
            size="40"
            testId={`access-reports-wizard__access-analysis--access-criteria-group-${groupIndex}-edit-${index}-link`}
          >
            <i className="fa fa-pencil-square-o fa-fw text-info" />
          </FlyoutLink>
        ),
        /* eslint-enable */
      }));

      return (
        <div className="access-repotrs-wizard-access-criterion-table">
          <Waffles<AccessAnalysisTableData>
            columns={this.tablesColumnConfig}
            data={data}
            disableProcessingOverlay={true}
            handleCheckboxChange={this.handleCheckboxChange}
            fullWidth={true}
            selectedKeys={this.state.selectedAccessCriterion.map((ac) => ac.id.toString())}
            showCheckboxes={groupKey === this.ungroupedKey ? true : false}
            testId={`access-reports-wizard-access-analysis-${groupKey}`}
          />
        </div>
      );
    }
  }

  private buildGroupedAccessCriterion = (): void => {
    const groupedAccessCriterion: Map<string, AccessReportsWizardAccessCriteria[]> = new Map<string, AccessReportsWizardAccessCriteria[]>();

    if (this.props.accessReportParameters && this.props.accessReportParameters.product && this.props.accessReportParameters.standard && this.props.accessReportParameters.calculationMethod) {
      for (const accessCriteria of this.props.accessReportParameters.product.accessCriterion) {
        if (accessCriteria.groups && accessCriteria.groups.length > 0) {
          // grouped access criterion
          for (const group of accessCriteria.groups) {
            const existingMapping: AccessReportsWizardAccessCriteria[] = groupedAccessCriterion.get(group);

            if (existingMapping) {
              // if there is an existing mapping for this group key,
              // add to it, rather than overwriting it
              if (!existingMapping.includes(accessCriteria)) {
                existingMapping.push(accessCriteria);
                groupedAccessCriterion.set(group, existingMapping);
              }
            } else {
              groupedAccessCriterion.set(group, [accessCriteria]);
            }
          }
        } else {
          // ungrouped access criterion
          const existingMapping: AccessReportsWizardAccessCriteria[] = groupedAccessCriterion.get(this.ungroupedKey);

          if (existingMapping) {
            // if there is an existing mapping for this group key,
            // add to it, rather than overwriting it
            if (!existingMapping.includes(accessCriteria)) {
              existingMapping.push(accessCriteria);
              groupedAccessCriterion.set(this.ungroupedKey, existingMapping);
            }
          } else {
            groupedAccessCriterion.set(this.ungroupedKey, [accessCriteria]);
          }
        }
      }
    }

    const selectedGroupKey: string = this.getSelectedGroupKey(groupedAccessCriterion);
    // only include previously-selected access criterion if ungrouped 
    const selectedAccessCriterion: AccessReportsWizardAccessCriteria[] = this.props.accessReportParameters.product.accessCriterion.filter((x) => x.selected && (!x.groups || !x.groups.length));

    this.setState({
      groupedAccessCriterion,
      selectedAccessCriterion,
      selectedGroupKey,
    });
  }

  private getColumnConfig = (): ColumnConfig => {
    return ({
      edit: {
        displayText: "Edit",
        hidden: !this.props.accessReportParameters.product.editable,
        noSort: true,
        testId: "edit",
      },
      standards: {
        displayText: "Standards",
        noSort: true,
        testId: "standards",
      },
      type: {
        displayText: "Type",
        noSort: true,
        testId: "type"
      },
    });
  }

  private getSelectedGroupKey = (groupedAccessCriterion: Map<string, AccessReportsWizardAccessCriteria[]>): string => {
    let selectedGroupKey = "";
    let selectedGroupKeyLength = 0;

    if (groupedAccessCriterion.size) {
      for (const [groupKey, accessCriterion] of groupedAccessCriterion) {
        const selectedAccessCriterion: AccessReportsWizardAccessCriteria[] = accessCriterion.filter((x) => x.selected);

        if (selectedAccessCriterion.length === accessCriterion.length) {
          if (!selectedGroupKey) {
            selectedGroupKey = groupKey;
          } else {
            if (selectedAccessCriterion.length > selectedGroupKeyLength) {
              selectedGroupKey = groupKey;
            }
          }

          selectedGroupKeyLength = accessCriterion.length;
        }
      }
    }

    return selectedGroupKey;
  }

  private getSortValue = (a: string, b: string) => {
    if (a === this.ungroupedKey) {
      // ungrouped access criterion should sort after everything else
      return 1;
    } else if (b === this.ungroupedKey) {
      // ungrouped access criterion should sort after everything else
      return -1;
    } else {
      // sort by a group key's number (if it has one), then alphabetically
      // first, split the group key by whitespace,
      // also completely removing any whitespace
      // to avoid selecting it in the next step
      const splitGroupKeyA: string[] = a.split(/(\s+)/).filter((x) => {
        return x.trim().length > 0;
      });
      const splitGroupKeyB: string[] = b.split(/(\s+)/).filter((x) => {
        return x.trim().length > 0;
      });

      // next, select the first split that is a number
      const numberA: string = splitGroupKeyA.find(x => !isNaN(Number(x)));
      const numberB: string = splitGroupKeyB.find(x => !isNaN(Number(x)));

      if (numberA && numberB) {
        // comparing two numbers
        return a < b ? -1 : 1;
      } else if (numberA) {
        // a is a number
        return 1;
      } else if (numberB) {
        // b is a number
        return -1;
      } else {
        // comparing two letters
        return a < b ? -1 : 1;
      }
    }
  }

  private handleCheckboxChange = (selectedKeys: string[]): void => {
    const selectedAccessCriterion: AccessReportsWizardAccessCriteria[] =
      this.props.accessReportParameters.product.accessCriterion.filter((x) => selectedKeys.includes(x.id.toString()));

    this.props.onAccessCriteriaCheckboxToggle(
      selectedKeys,
      this.state.selectedGroupKey,
    );

    this.setState({
      selectedAccessCriterion,
    });
  }

  private onGroupedAccessCriterionClick = (groupKey: string): void => {
    this.props.onGroupedAccessCriterionClick(
      groupKey,
      this.state.selectedAccessCriterion.map((ac: AccessReportsWizardAccessCriteria) => ac.id.toString()),
    );

    this.setState({
      selectedGroupKey: groupKey,
    });
  }

  private onProductChange = (accessCriteria: AccessReportsWizardAccessCriteria): void => {
    this.props.onProductChange(accessCriteria);

    if (this.state.sidePanelVisible) {
      this.setState({
        sidePanelVisible: false,
      });
    }
  }

  private renderEditNetworkSidePanel = (accessCriteria: AccessReportsWizardAccessCriteria): React.ReactNode => {
    return (
      <AccessReportsWizardEditNetworks
        accessCriteria={accessCriteria}
        accessReportParameters={this.props.accessReportParameters}
        onProductChange={this.onProductChange}
        product={this.props.accessReportParameters.product}
      />
    );
  }
}

export { AccessReportsWizardStepAccessAnalysis };
